import { useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const services = [
  {
    slug: "estate-tour",
    title: "Estate Tour",
    lede: "Walk through the estate with our master planters.",
    duration: "Half day · 4 hrs",
    price: "From ₹2,500 per guest",
    image: "https://images.unsplash.com/photo-1648483880685-356ea43870c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    detail: "Explore the shade-grown coffee plantation, meet the pickers at harvest time, and understand how altitude, terroir, and processing shape the cup.",
  },
  {
    slug: "roasting-workshop",
    title: "Roasting Workshop",
    lede: "From green bean to first crack.",
    duration: "Full day · 6 hrs",
    price: "From ₹6,500 per guest",
    image: "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    detail: "Roast your own micro-batch alongside our head roaster. Take home 500g of coffee you crafted yourself.",
  },
  {
    slug: "barista-training",
    title: "Barista Training",
    lede: "Extraction, milk, and latte art.",
    duration: "Two days",
    price: "From ₹12,500 per guest",
    image: "https://images.unsplash.com/photo-1522012188892-24beb302783d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    detail: "Hands-on with commercial equipment. Learn extraction, milk texturing, and latte art from SCA-certified trainers.",
  },
];

export default function Services() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="services-page" className="pt-32 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 mb-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">Experiences</div>
        <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[0.95]">
          Meet the estate<br />that grew your coffee.
        </h1>
        <p className="mt-6 max-w-2xl text-[#4A3B32] text-lg leading-relaxed">
          Three ways to go deeper — from a half-day walk to a two-day barista intensive. Small
          groups, personal attention, unforgettable coffee.
        </p>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 space-y-16">
        {services.map((s, i) => (
          <div
            key={s.slug}
            data-testid={`service-card-${s.slug}`}
            className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center ${
              i % 2 === 1 ? "md:direction-rtl" : ""
            }`}
          >
            <div className={`md:col-span-7 aspect-[16/10] overflow-hidden ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
            </div>
            <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1" : ""}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">
                {s.duration}
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-none">
                {s.title}
              </h2>
              <p className="mt-4 text-[#7B6F66]">{s.lede}</p>
              <p className="mt-6 text-[#4A3B32] leading-relaxed">{s.detail}</p>
              <div className="mt-6 text-lg font-medium text-[#1C3326]">{s.price}</div>
              <button
                onClick={() => setActive(s)}
                data-testid={`book-${s.slug}`}
                className="mt-6 bg-[#1C3326] hover:bg-[#15261C] text-[#FDFBF7] px-10 py-4 uppercase text-xs tracking-[0.3em] font-medium transition-colors"
              >
                Book now
              </button>
            </div>
          </div>
        ))}
      </div>

      {active && <BookingModal service={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function BookingModal({ service, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", guests: 1, notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/bookings", { ...form, service: service.slug, guests: parseInt(form.guests) });
      toast.success("Booking request received — we'll be in touch.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to book");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#2C1E16]/70 backdrop-blur-sm flex items-center justify-center px-6"
      onClick={onClose}
      data-testid="booking-modal"
    >
      <div className="bg-[#FDFBF7] max-w-lg w-full p-8 md:p-10" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">Book</div>
        <h3 className="font-serif-display text-3xl md:text-4xl font-light mb-6">{service.title}</h3>
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="booking-name" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="booking-email" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <input required placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="booking-phone" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <div className="grid grid-cols-2 gap-4">
            <input required type="date" value={form.date} onChange={set("date")} data-testid="booking-date" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
            <input required type="number" min={1} max={20} placeholder="Guests" value={form.guests} onChange={set("guests")} data-testid="booking-guests" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          </div>
          <textarea rows={3} placeholder="Notes" value={form.notes} onChange={set("notes")} data-testid="booking-notes" className="w-full border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none bg-transparent" />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-[#1C3326] px-6 py-4 uppercase text-xs tracking-[0.3em]"
              data-testid="booking-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] px-6 py-4 uppercase text-xs tracking-[0.3em] disabled:opacity-60"
              data-testid="booking-submit"
            >
              {busy ? "Sending…" : "Request Booking"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
